/**
 * Shared device-status helpers.
 *
 * The staleness maths itself deliberately stays at each call site: the profile
 * tabs compare `new Date(last_active)` directly, while the devices tables convert
 * from campus-local first (`campusLocalToMs(row.last_active, tzOffset)`). Those are
 * genuinely different, so only the exemption rule is shared.
 *
 * The muted badge, by contrast, IS shared whole — see MUTED_BADGE below.
 */

import { isMuted } from './deviceMute.js';

/**
 * Should the stale check be skipped for this device's manufacturer?
 *
 * Some manufacturers' devices never heartbeat into the system, so `last_active`
 * only ever moves when the device is actually used — or never at all. Running the
 * 30-hour stale test against them flags healthy hardware yellow and tells staff to
 * go test a device that is fine, which trains them to ignore the warning. For those
 * manufacturers "active" means active, full stop.
 *
 * The exempt set is passed in rather than fixed here because each surface shows a
 * different device population, and the sets were specified per surface. If a
 * manufacturer turns out never to heartbeat anywhere, the honest fix is to widen
 * every caller's set rather than to special-case one screen.
 *
 * `manufacture_id` arrives as a number from some procedures and a string from
 * others (see WanderingDevicesTable, which tests `=== 6 || === '6'`), so it is
 * coerced rather than compared strictly.
 *
 * @param {number|string} manufactureId
 * @param {Array<number>} exemptIds — manufacturers exempt on this surface
 * @returns {boolean}
 */
export function skipsStaleCheck(manufactureId, exemptIds) {
  const mid = Number(manufactureId);
  if (!Number.isFinite(mid)) return false;
  return (exemptIds || []).includes(mid);
}

/**
 * The "Muted" status badge, shared by every surface that shows device status.
 *
 * Green, because the device genuinely is active and reporting — muting does not
 * take it offline. The label and the amber ring carry the caveat: it will not raise
 * an alert while suppressed. Saying "Active" here would assert something false, and
 * colouring it grey or yellow would assert the device is unhealthy, which it is
 * not.
 *
 * Defined once so all four surfaces read identically. The wording matters more than
 * usual: a badge that says a device is fine when it cannot alert anyone is exactly
 * the kind of thing staff act on.
 */
export const MUTED_BADGE = {
  className: 'dp-status--muted',
  label: 'Muted',
  title: 'Active but suppressed — the device is online and reporting normally, '
    + 'but alarms are suppressed so it will not raise an alert until the suppression ends.',
};

/**
 * Does the muted badge apply, i.e. is the device both active and suppressed?
 *
 * Takes precedence over the stale check at every call site. Suppression is a
 * deliberate state someone set minutes ago and can undo; staleness is an inference
 * from a timestamp. When both are true the suppression is the thing to say.
 *
 * Returns false for an inactive device: an unassigned device is already
 * deactivated, so "Unassigned" is the whole truth and a leftover away_until on it
 * is not worth reporting.
 *
 * @param {number|string|boolean} isActive — is_active, possibly a MySQL tinyint
 * @param {string} awayUntil — core_device.away_until, UTC 'YYYY-mm-dd HH:MM:SS'
 * @param {number} [nowMs] — injectable for tests
 * @returns {boolean}
 */
export function showsMuted(isActive, awayUntil, nowMs = Date.now()) {
  /* Number(), not truthiness: is_active is a tinyint and "0" is truthy in JS. */
  if (!Number(isActive)) return false;
  return isMuted(awayUntil, nowMs);
}
